import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {SettingsService} from '@src/app/core/services/settings.service';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { SiteFormDetail, SiteFormSummary } from '../models/site.model';
import { BaseService } from '@src/app/shared/services/base.service';

export interface SiteFormOptionGroup {
    serviceId: number | null;
    label: string;
    forms: SiteFormSummary[];
}

@Injectable({ providedIn: 'root' })
export class SiteFormsService extends BaseService {
    private readonly settings = inject(SettingsService);
    private static readonly UNASSIGNED_LABEL = 'Unassigned Service';

    public constructor(private readonly httpClient: HttpClient) {
        super();
    }

    public loadFormGroups(retainedFormIds: string[] = []): Observable<SiteFormOptionGroup[]> {
        const params = new HttpParams()
            .set('include', 'service')
            .set('page[size]', '500');

        const loadPage = (page: number) => this.httpClient.get<{data?: SiteFormSummary[]; meta?: {current_page: number; total_pages: number}}>(`${this.apiUrl}/forms`, {params: params.set('page[number]', page)});

        return loadPage(1).pipe(
            expand(response => response.meta && response.meta.current_page < response.meta.total_pages ? loadPage(response.meta.current_page + 1) : EMPTY),
            reduce((forms, response) => [...forms, ...(response.data ?? [])], [] as SiteFormSummary[]),
            map(forms => this.groupForms(forms.filter(form => this.settings.isServiceEnabled(form.service?.id) || retainedFormIds.includes(String(form.id)))))
        );
    }

    public loadFormDetails(formId: string): Observable<SiteFormDetail> {
        return this.httpClient
            .get<{data: SiteFormDetail}>(`${this.apiUrl}/forms/${formId}`)
            .pipe(map((response) => response.data));
    }

    private groupForms(forms: SiteFormSummary[]): SiteFormOptionGroup[] {
        const groups = forms.reduce<Record<string, SiteFormOptionGroup>>((acc, form) => {
            const label = (form.service?.name ?? SiteFormsService.UNASSIGNED_LABEL).trim();

            if (!acc[label]) {
                acc[label] = {
                    serviceId: form.service?.id ?? null,
                    label,
                    forms: [],
                };
            }

            acc[label].forms.push(form);
            return acc;
        }, {});

        return Object.values(groups)
            .map((group) => ({
                ...group,
                forms: [...group.forms].sort((a, b) => a.title.localeCompare(b.title)),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }
}
